'use strict'

import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { CircularProgress, IconButton, Tooltip } from '@material-ui/core'
import RepeatIcon from '@material-ui/icons/Repeat'
import CloseIcon from '@material-ui/icons/Close'
import { formatAnchorLabel, formatShortDate } from './cadenceAnchorOptions'

// Long-Cycle Planner drawer: one row per long-cycle (M/Q/SA/A) property in
// the book, with its cadence-anchor state and read-time health badges from
// GET /territory/long-cycle (longCycle.controller.js). Scenario-agnostic —
// the rows are live-level data; scenarioId only feeds the no-slack badge
// (latest completed auto-optimize run for that scenario).
//
// v1 deliberately has NO "Set anchor…" action here: creating an anchor goes
// through the landing dialog, which needs a live drag context (a concrete
// landing date/route from dropping the visit on a day bucket). The drawer
// only re-applies or clears anchors that already exist.

const BADGE_STYLES = {
  'off-anchor': {
    background: 'var(--df-warn-bg, #fbe8d6)',
    color: 'var(--df-warn-text, #a4502f)',
    title: 'Live visit dates have drifted from the anchor pattern'
  },
  'companion-moved': {
    background: 'var(--df-warn-bg, #fbe8d6)',
    color: 'var(--df-warn-text, #a4502f)',
    title: 'The companion property this anchor was set from no longer matches its snapshot'
  },
  'no-slack': {
    background: 'var(--df-danger-bg, #fdecea)',
    color: 'var(--df-danger-text, #b3261e)',
    title: 'Last auto-optimize run found no capacity slack inside this visit\'s cadence window'
  },
  unpushed: {
    background: 'var(--df-info-bg, #e3ecf6)',
    color: 'var(--df-info-text, #2a5b8f)',
    title: 'Override date differs from Aspire\'s — not yet pushed/published'
  }
}

function BadgePill ({ badge }) {
  const style = BADGE_STYLES[badge] || {
    background: 'var(--df-surface-2, #f4efe4)',
    color: 'var(--df-ink-3, #5c6657)',
    title: badge
  }
  return (
    <span
      className="rw-pill"
      title={style.title}
      style={{ background: style.background, color: style.color, fontSize: 10.5, fontWeight: 600 }}
    >
      {badge}
    </span>
  )
}

const smallBtnStyle = {
  fontSize: 10.5,
  fontWeight: 700,
  padding: '3px 8px',
  borderRadius: 6,
  border: '1px solid var(--df-line, #e0d8c2)',
  background: 'var(--df-surface, #fbf8f1)',
  color: 'var(--df-ink-2, #3d463a)',
  cursor: 'pointer',
  whiteSpace: 'nowrap'
}

export default function LongCyclePlannerDrawer ({ url, scenarioId, onSnackbar, onClose }) {
  const [state, setState] = useState({ loading: true, error: null, rows: [] })
  // aspirePropertyId -> 'reapply' | 'clear' while that row's action is in flight
  const [busyByPid, setBusyByPid] = useState({})

  const fetchRows = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const res = await axios.get(`${url}/api/hmi/territory/long-cycle`, {
        params: scenarioId ? { scenarioId } : {}
      })
      setState({ loading: false, error: null, rows: res.data?.rows || [] })
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to load long-cycle properties'
      setState({ loading: false, error: message, rows: [] })
      if (onSnackbar) onSnackbar(message, 'error')
    }
  }, [url, scenarioId, onSnackbar])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const reapplyAnchor = async (row) => {
    const anchor = row.anchor
    if (!anchor) return
    setBusyByPid(prev => ({ ...prev, [row.aspirePropertyId]: 'reapply' }))
    try {
      // Re-POST the anchor's own fields: the server upsert is replace-
      // semantics + idempotent apply, so this simply re-projects the pattern
      // over the property's current future visits (healing off-anchor drift).
      const res = await axios.post(`${url}/api/hmi/territory/long-cycle/cadence-anchor`, {
        aspirePropertyId: row.aspirePropertyId,
        frequencyCode: anchor.frequencyCode || row.frequencyCode || null,
        anchorType: anchor.anchorType,
        params: anchor.params || {},
        pinScope: anchor.pinScope || 'day_only',
        routeId: anchor.routeId != null ? anchor.routeId : null,
        crewId: anchor.crewId || null,
        opportunityServiceId: anchor.opportunityServiceId != null ? anchor.opportunityServiceId : null,
        provenance: anchor.provenance || {}
      })
      const results = res.data?.results || []
      const redated = results.filter(r => !r.skipped && r.to).length
      const skipped = results.filter(r => r.skipped).length
      if (onSnackbar) {
        onSnackbar(
          `Anchor re-applied: ${redated} visit${redated === 1 ? '' : 's'} re-dated` +
            (skipped ? ` · ${skipped} skipped (manual overrides)` : ''),
          'success'
        )
      }
      await fetchRows()
    } catch (err) {
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to re-apply cadence anchor', 'error')
    } finally {
      setBusyByPid(prev => {
        const next = { ...prev }
        delete next[row.aspirePropertyId]
        return next
      })
    }
  }

  const clearAnchor = async (row) => {
    const anchor = row.anchor
    if (!anchor?._id) return
    const name = row.propertyName || `Aspire #${row.aspirePropertyId}`
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Clear the cadence anchor for ${name}? Future visits become flexible; dates already applied stay as-is.`)) return
    setBusyByPid(prev => ({ ...prev, [row.aspirePropertyId]: 'clear' }))
    try {
      await axios.delete(`${url}/api/hmi/territory/long-cycle/cadence-anchor/${anchor._id}`)
      if (onSnackbar) onSnackbar(`Cadence anchor cleared for ${name}`, 'success')
      await fetchRows()
    } catch (err) {
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to clear cadence anchor', 'error')
    } finally {
      setBusyByPid(prev => {
        const next = { ...prev }
        delete next[row.aspirePropertyId]
        return next
      })
    }
  }

  const rows = state.rows || []
  const anchoredCount = rows.filter(row => row.anchor).length

  return (
    <div className="cap-drawer" style={{
      position: 'absolute', top: 0, right: 0, bottom: 0, width: 420, zIndex: 3000,
      background: 'var(--df-surface, #fbf8f1)',
      borderLeft: '1px solid var(--df-line, #e0d8c2)',
      boxShadow: '-8px 0 28px rgba(28,36,25,0.12)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      animation: 'df-drawerIn 0.26s cubic-bezier(.16,1,.3,1)'
    }}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--df-line, #e0d8c2)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RepeatIcon style={{ fontSize: 18, color: 'var(--df-primary, #1f5e3a)' }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink, #1c2419)', flex: 1 }}>
            Long-Cycle Planner
          </span>
          {state.loading && <CircularProgress size={14} style={{ color: 'var(--df-primary, #1f5e3a)' }} />}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
        <div style={{ fontSize: 11, color: 'var(--df-ink-4, #97a094)', marginTop: 4 }}>
          Monthly / quarterly / semi-annual / annual visits — cadence anchors &amp; health badges
        </div>
      </div>

      {/* Body */}
      <div style={{ overflowY: 'auto', flex: 1, padding: '4px 16px 12px' }}>
        {state.loading && rows.length === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 0' }}>
            <CircularProgress size={18} style={{ color: 'var(--df-primary, #1f5e3a)' }} />
            <span style={{ fontSize: 13, color: 'var(--df-ink-4, #97a094)' }}>Loading long-cycle properties…</span>
          </div>
        )}

        {!state.loading && state.error && (
          <div style={{ padding: '18px 0' }}>
            <div style={{ fontSize: 13, color: 'var(--df-danger-text, #b3261e)', marginBottom: 10 }}>
              {state.error}
            </div>
            <button type="button" style={smallBtnStyle} onClick={fetchRows}>
              Retry
            </button>
          </div>
        )}

        {!state.loading && !state.error && rows.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--df-ink-4, #97a094)', padding: '18px 0' }}>
            No long-cycle properties in this book.
          </div>
        )}

        {!state.error && rows.map((row, index) => {
          const busy = busyByPid[row.aspirePropertyId]
          const anchorLabel = row.anchor ? formatAnchorLabel(row.anchor) : ''
          const canReapply = !!row.anchor && (row.badges || []).includes('off-anchor')
          return (
            <div
              key={row.aspirePropertyId}
              style={{
                padding: '10px 0',
                borderBottom: index < rows.length - 1 ? '1px solid var(--df-line-soft, #ede6d2)' : 'none'
              }}
            >
              {/* Line 1: name · freq · next date */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{
                  fontSize: 12.5, fontWeight: 700, color: 'var(--df-ink, #1c2419)',
                  flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {row.propertyName || `Aspire #${row.aspirePropertyId}`}
                </span>
                {row.frequencyCode && (
                  <span className="rw-pill pill-freq" style={{ flexShrink: 0 }}>{row.frequencyCode}</span>
                )}
                <span style={{ fontSize: 11.5, color: 'var(--df-ink-2, #3d463a)', fontWeight: 600, flexShrink: 0 }}>
                  {row.nextDate ? formatShortDate(row.nextDate) : '—'}
                </span>
              </div>

              {/* Line 2: route · anchor summary */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 3 }}>
                <span style={{
                  fontSize: 11, color: 'var(--df-ink-4, #97a094)',
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {row.routeName || 'No route'}
                </span>
                <span style={{ fontSize: 11, color: 'var(--df-ink-3, #5c6657)', marginLeft: 'auto', flexShrink: 0 }}>
                  {row.anchor
                    ? `${anchorLabel || row.anchor.anchorType}${row.anchor.pinScope === 'crew_day' ? ' · crew-pinned' : ''}`
                    : <em style={{ color: 'var(--df-ink-4, #97a094)' }}>Flexible</em>}
                </span>
              </div>

              {/* Line 3: badges + actions */}
              {((row.badges || []).length > 0 || row.anchor) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  {(row.badges || []).map(badge => <BadgePill key={badge} badge={badge} />)}
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    {busy && <CircularProgress size={12} style={{ color: 'var(--df-primary, #1f5e3a)' }} />}
                    {canReapply && (
                      <button
                        type="button"
                        style={{ ...smallBtnStyle, ...(busy ? { opacity: 0.6, cursor: 'default' } : {}) }}
                        disabled={!!busy}
                        title="Re-project this anchor over the property's future visits (heals off-anchor drift)"
                        onClick={() => reapplyAnchor(row)}
                      >
                        Re-apply
                      </button>
                    )}
                    {row.anchor && (
                      <button
                        type="button"
                        style={{ ...smallBtnStyle, ...(busy ? { opacity: 0.6, cursor: 'default' } : {}) }}
                        disabled={!!busy}
                        title="Deactivate the anchor — future visits become flexible"
                        onClick={() => clearAnchor(row)}
                      >
                        Clear
                      </button>
                    )}
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Footer count line */}
      <div style={{
        padding: '10px 16px', borderTop: '1px solid var(--df-line, #e0d8c2)', flexShrink: 0,
        fontSize: 11.5, color: 'var(--df-ink-3, #5c6657)'
      }}>
        {rows.length} long-cycle propert{rows.length === 1 ? 'y' : 'ies'} · {anchoredCount} anchored
      </div>
    </div>
  )
}
